/**
 * dayWheel — pure geometry for the C5 day-wheel instrument.
 *
 * The wheel is a painted disc inside a dark ring with a fixed pointer at the
 * top. The disc rotates so that the segment matching the current day fraction
 * always sits under the pointer.
 *
 * Conventions (all angles in degrees, clockwise, 0 = straight up):
 *   - disc-local angle of a day fraction f  = f * 360
 *   - disc rotation for fraction f          = -f * 360
 *   → the point authored at f appears at the top (pointer) when the clock
 *     reads f. Sun and moon are placed at their own fractions, so they rise
 *     and set as the disc turns.
 *
 * The segment boundaries come from `TOD_BOUNDARIES` in @game/shared, so the
 * painted disc can never drift from the phase the rest of the game believes
 * it is in. `phaseAtPointer()` is proven equal to `getTimeOfDay()` by test.
 */

import { TOD_BOUNDARIES, getTimeOfDay, type TimeOfDay } from '@game/shared';

/**
 * In-fiction clock offset, in hours.
 *
 * `TOD_BOUNDARIES` puts dawn at fraction 0. A raw `fraction * 24` clock would
 * therefore print "00:00" while the sky is doing sunrise, which reads as a
 * bug to a player. Shifting the printed clock by +5h puts dawn at 05:00,
 * midday around 13:00 and night after 21:00 — the phase names and the numbers
 * agree. Only the printed chip is shifted; nothing else uses this.
 */
export const CLOCK_OFFSET_HOURS = 5;

/** Normalise any fraction into [0, 1). */
export function normalizeFraction(fraction: number): number {
  if (!Number.isFinite(fraction)) return 0;
  return ((fraction % 1) + 1) % 1;
}

/** Disc-local angle (deg, clockwise from top) for a day fraction. */
export function fractionToDeg(fraction: number): number {
  return normalizeFraction(fraction) * 360;
}

/** CSS rotation (deg) to apply to the disc for a given day fraction. */
export function discRotationDeg(fraction: number): number {
  return -fractionToDeg(fraction);
}

export interface WheelSegment {
  phase: TimeOfDay;
  /** Inclusive start angle, degrees clockwise from disc top. */
  startDeg: number;
  /** Exclusive end angle. */
  endDeg: number;
}

/** The four painted segments, derived from the shared phase boundaries. */
export function wheelSegments(): WheelSegment[] {
  const { DAWN_START, DAY_START, DUSK_START, NIGHT_START } = TOD_BOUNDARIES;
  return [
    { phase: 'dawn',  startDeg: DAWN_START * 360,  endDeg: DAY_START * 360 },
    { phase: 'day',   startDeg: DAY_START * 360,   endDeg: DUSK_START * 360 },
    { phase: 'dusk',  startDeg: DUSK_START * 360,  endDeg: NIGHT_START * 360 },
    { phase: 'night', startDeg: NIGHT_START * 360, endDeg: 360 },
  ];
}

/**
 * Which phase the fixed pointer is reading for a given fraction. Must always
 * equal `getTimeOfDay(fraction)` — that equality is the alignment guarantee
 * between the painted disc and the simulation.
 */
export function phaseAtPointer(fraction: number): TimeOfDay {
  const deg = fractionToDeg(fraction);
  for (const seg of wheelSegments()) {
    if (deg >= seg.startDeg && deg < seg.endDeg) return seg.phase;
  }
  return 'night';
}

/** Sanity hook used by tests: the segments must tile [0, 360) exactly once. */
export function segmentsAreContiguous(): boolean {
  const segs = wheelSegments();
  if (segs[0].startDeg !== 0) return false;
  if (segs[segs.length - 1].endDeg !== 360) return false;
  for (let i = 1; i < segs.length; i++) {
    if (segs[i].startDeg !== segs[i - 1].endDeg) return false;
    if (segs[i].endDeg <= segs[i].startDeg) return false;
  }
  return true;
}

/** In-fiction clock string "HH:MM" for a day fraction. */
export function formatClock(fraction: number): string {
  const hoursFloat = (normalizeFraction(fraction) * 24 + CLOCK_OFFSET_HOURS) % 24;
  let h = Math.floor(hoursFloat);
  let m = Math.floor((hoursFloat - h) * 60);
  if (m >= 60) {
    m = 0;
    h = (h + 1) % 24;
  }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Moon illumination 0..1 from a lunar phase 0..1 (0 = new, 0.5 = full).
 * Used to fade the moon dot; a crescent shape pass can come later.
 */
export function moonIllumination(lunarPhase: number): number {
  const p = normalizeFraction(lunarPhase);
  return 1 - Math.abs(0.5 - p) * 2;
}

/** Discrete moon step name for CSS class selection. */
export type MoonStep = 'new' | 'crescent' | 'half' | 'gibbous' | 'full';

export function moonStep(lunarPhase: number): MoonStep {
  const lit = moonIllumination(lunarPhase);
  if (lit < 0.12) return 'new';
  if (lit < 0.38) return 'crescent';
  if (lit < 0.62) return 'half';
  if (lit < 0.88) return 'gibbous';
  return 'full';
}

/**
 * Where the sun and moon sit on the disc, as day fractions.
 * Sun peaks at the middle of the `day` window; the moon sits opposite.
 */
export const SUN_FRACTION =
  (TOD_BOUNDARIES.DAY_START + TOD_BOUNDARIES.DUSK_START) / 2;
export const MOON_FRACTION = normalizeFraction(SUN_FRACTION + 0.5);

/** Re-exported for callers that want to assert wheel/sim agreement. */
export { getTimeOfDay };
